import { Button } from '@/ui/components/Button';
import { ToggleSwitchCard } from '@/ui/components/ToggleSwitchCard';
import { checkMarkButton } from '@/assets/images/emojis';
import styles from './OnboardingTermsPage.module.scss';
import { Icon } from '@/ui/icons/Icon';
import { routes } from '@/routes/routes';
import { Link } from 'react-router-dom';
import { useTerms } from '@/features/terms/hooks/useTerms';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '@/features/onboarding';
import type { TermsState } from '@/features/terms';
import {
  NotificationCategory,
  NotificationType,
  useNotifications,
} from '@/features/notifications';
import { useLogger } from '@/lib/logger';
import { useOfferRequest } from '@/features/offer';

function OnboardingTermsPage() {
  const navigate = useNavigate();
  const logger = useLogger();
  const {
    acceptBcTerms,
    acceptPartnerAnafTerms,
    isLoading: isTermsLoading,
    acceptedTerms,
    setAcceptedTerms,
    clearAcceptedTerms,
  } = useTerms();

  const {requestOffer, isLoading: isOfferLoading} = useOfferRequest();
  const { getOnboardingState } = useOnboarding();
  const { isUpdating: isUpdatingNotifications, updateNotificationSettings } =
    useNotifications();

  const handleTermChange = (
    termKey: keyof Omit<TermsState, 'selectAll'>,
    checked: boolean
  ) => {
    const newTerms = {
      ...acceptedTerms,
      [termKey]: checked,
    };

    const allSelected = newTerms.notifications && newTerms.bc && newTerms.anaf;

    newTerms.selectAll = allSelected;

    setAcceptedTerms(newTerms);
  };

  const handleSelectAllChange = (checked: boolean) => {
    setAcceptedTerms({
      selectAll: checked,
      notifications: checked,
      bc: checked,
      anaf: checked,
    });
  };

  // Only bc and anaf terms are required to continue
  const isLoading = isTermsLoading || isUpdatingNotifications || isOfferLoading;

  const isContinueDisabled =
    !acceptedTerms.bc || !acceptedTerms.anaf || isLoading;

  const handleContinue = async () => {
    if (isContinueDisabled) {
      return;
    }

    try {
      await acceptBcTerms();
      await acceptPartnerAnafTerms();
      await requestOffer();

      if (!acceptedTerms.notifications) {
        await updateNotificationSettings({
          category: NotificationCategory.Email,
          type: NotificationType.Marketing,
          isEnabled: false,
        });
      }

      await getOnboardingState();

      clearAcceptedTerms();
      navigate(routes.onboarding.cnp);
    } catch (error) {
      logger.error('Failed to accept terms', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.emojiContainer}>
            <img
              src={checkMarkButton}
              alt="Confirm agreement"
              className={styles.emoji}
            />
          </div>
          <div className={styles.textContainer}>
            <h1 className={styles.title}>Confirmă acordul tău</h1>
            <p className={styles.subtitle}>
              Selectează opțiunile de mai jos pentru a continua.
            </p>
          </div>
        </div>

        {/* Terms Section */}
        <div className={styles.termsSection}>
          {/* Select All Card */}
          <ToggleSwitchCard
            isSelectAll
            checked={acceptedTerms.selectAll}
            label="Selectați tot"
            onChange={handleSelectAllChange}
            testId="select-all"
            className={styles.selectAllCard}
          />

          {/* Notifications */}
          <ToggleSwitchCard
            checked={acceptedTerms.notifications}
            label="Confirm că sunt de acord să primesc notificări referitoare la campanii promoționale și oferte de credit"
            onChange={(checked) => handleTermChange('notifications', checked)}
            testId="notifications"
          />

          {/* Credit Bureau */}
          <div className={styles.termsItem}>
            <ToggleSwitchCard
              checked={acceptedTerms.bc}
              label="Sunt de acord cu interogarea la Biroul de credit"
              onChange={(checked) => handleTermChange('bc', checked)}
              testId="bc"
            />
            <div className={styles.linkContainer}>
              <Link to={routes.terms.bc} className={styles.link}>
                Termeni și condiții Biroul de Credit
              </Link>
            </div>
          </div>

          {/* ANAF */}
          <div className={styles.termsItem}>
            <ToggleSwitchCard
              checked={acceptedTerms.anaf}
              label="Sunt de acord cu interogarea la ANAF"
              onChange={(checked) => handleTermChange('anaf', checked)}
              testId="anaf"
            />
            <div className={styles.linkContainer}>
              <Link to={routes.terms.anaf} className={styles.link}>
                Termeni și condiții ANAF
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <Button
          trackingId="accept_onboarding_terms"
          variant="filled"
          text="Continuă"
          className={styles.button}
          onClick={handleContinue}
          disabled={isContinueDisabled}
          loading={isLoading}
          rightIcon={<Icon name="arrow-right" />}
        />
      </div>
    </div>
  );
}

export default OnboardingTermsPage;
